import React, { useMemo } from 'react';
import { styled, alpha } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { DrawerWidth } from '../../utils/constants.js';
import { SideBarItemsForAdmin, SideBarItemsForStudent } from './SiteBarItems.jsx';
import { NavLink } from 'react-router-dom';
import { Tooltip } from '@mui/material';

// Dark theme colors - black, white, gray, red
const sidebarColors = {
    background: 'linear-gradient(180deg, #1a1a1a 0%, #121212 50%, #0a0a0a 100%)',
    backgroundSolid: '#1a1a1a',
    activeBackground: 'linear-gradient(90deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%)',
    activeBorder: '#ef4444',
    hoverBackground: 'rgba(255, 255, 255, 0.06)',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    iconColor: 'rgba(255, 255, 255, 0.7)',
    iconActiveColor: '#ef4444',
    divider: 'rgba(255, 255, 255, 0.08)',
};

const openedMixin = (theme) => ({
    width: DrawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

export const DrawerHeader = styled('div')(({ theme }) => ({
    background: sidebarColors.backgroundSolid,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));


const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme }) => ({
        width: DrawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiPaper-root': {
            background: sidebarColors.background,
            color: sidebarColors.textPrimary,
            borderRight: 'none',
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.3)',
        },
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    ...openedMixin(theme),
                    '& .MuiDrawer-paper': openedMixin(theme),
                },
            },
            {
                props: ({ open }) => !open,
                style: {
                    ...closedMixin(theme),
                    '& .MuiDrawer-paper': closedMixin(theme),
                },
            },
        ],
    }),
);



export const AppSidebar = ({ open, handleDrawerClose }) => {
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem('userDetails')), []);
    const userRole = userDetails?.role;
    const menuItems = useMemo(() => userRole === "Admin" ? SideBarItemsForAdmin() : SideBarItemsForStudent(), [userRole])

    return (
        <Drawer variant="permanent" open={open}>
            <DrawerHeader>
                <IconButton
                    onClick={handleDrawerClose}
                    sx={{
                        color: sidebarColors.textSecondary,
                        '&:hover': {
                            color: sidebarColors.textPrimary,
                            backgroundColor: sidebarColors.hoverBackground
                        }
                    }}
                >
                    <ChevronLeftIcon />
                </IconButton>
            </DrawerHeader>
            <Divider sx={{ borderColor: sidebarColors.divider }} />
            <List sx={{ pt: 1, px: 1 }}>
                {menuItems
                    .filter(x => !x.hidden)
                    .map((x, index) => (
                        <SideBarItem key={x.label} open={open} item={x} index={index} />
                    ))}
            </List>
        </Drawer>
    );
};

export const AppMobileSidebar = ({ open, handleDrawerClose }) => {
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem('userDetails')), []);
    const userRole = userDetails?.role;
    const menuItems = useMemo(() => userRole === "Admin" ? SideBarItemsForAdmin() : SideBarItemsForStudent(), [userRole])

    return (
        <MuiDrawer
            variant="temporary"
            ModalProps={{ keepMounted: true }}
            open={open}
            onClose={handleDrawerClose}
            sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: DrawerWidth,
                    background: sidebarColors.background,
                    color: sidebarColors.textPrimary,
                    borderRight: 'none',
                    boxShadow: '4px 0 24px rgba(0, 0, 0, 0.3)',
                },
            }}
        >
            <DrawerHeader>
                <IconButton
                    onClick={handleDrawerClose}
                    sx={{
                        color: sidebarColors.textSecondary,
                        '&:hover': {
                            color: sidebarColors.textPrimary,
                            backgroundColor: sidebarColors.hoverBackground
                        }
                    }}
                >
                    <ChevronLeftIcon />
                </IconButton>
            </DrawerHeader>
            <Divider sx={{ borderColor: sidebarColors.divider }} />
            <List sx={{ pt: 1, px: 1 }}>
                {menuItems
                    .filter(x => !x.hidden)
                    .map((x, index) => (
                        <SideBarItem key={x.label} open={open} item={x} index={index} />
                    ))}
            </List>
        </MuiDrawer>
    );
};

const SideBarItem = ({ item, open, index }) => {
    return (
        <Tooltip
            title={!open ? item.label : ''}
            placement="right"
            arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: '#1a1a1a',
                        color: '#fff',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        '& .MuiTooltip-arrow': {
                            color: '#1a1a1a',
                        },
                    },
                },
            }}
        >
            <ListItem
                component={NavLink}
                to={item.path}
                disablePadding
                sx={{
                    display: 'block',
                    mb: 0.5,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease-in-out',

                    // Active state
                    '&.active': {
                        background: sidebarColors.activeBackground,
                        borderLeft: `3px solid ${sidebarColors.activeBorder}`,
                        '& .MuiListItemIcon-root': {
                            color: sidebarColors.iconActiveColor,
                        },
                        '& .MuiListItemText-primary': {
                            color: sidebarColors.textPrimary,
                            fontWeight: 600,
                        },
                    },

                    // Hover state
                    '&:hover:not(.active)': {
                        backgroundColor: sidebarColors.hoverBackground,
                        transform: 'translateX(4px)',
                        '& .MuiListItemIcon-root': {
                            color: sidebarColors.textPrimary,
                        },
                    },
                }}
            >
                <ListItemButton
                    sx={{
                        minHeight: 48,
                        px: 2,
                        py: 1.2,
                        justifyContent: open ? 'initial' : 'center',
                        borderRadius: '10px',
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            justifyContent: 'center',
                            mr: open ? 2.5 : 'auto',
                            color: sidebarColors.iconColor,
                            transition: 'all 0.2s ease-in-out',
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.35rem',
                            }
                        }}
                    >
                        {item.icon}
                    </ListItemIcon>
                    <ListItemText
                        primary={item.label}
                        sx={{
                            opacity: open ? 1 : 0,
                            transition: 'opacity 0.2s ease-in-out',
                            '& .MuiListItemText-primary': {
                                color: sidebarColors.textSecondary,
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                letterSpacing: '0.3px',
                            }
                        }}
                    />
                </ListItemButton>
            </ListItem>
        </Tooltip>
    )
}

